/**
 * Secure Token Storage Service
 *
 * Provides a more secure alternative to plain localStorage for JWT tokens.
 * Uses in-memory storage with encrypted persistence as fallback.
 *
 * Security improvements:
 * - Access tokens stored in memory (not accessible via XSS to localStorage)
 * - Refresh tokens encrypted before storage
 * - Automatic token rotation tracking
 * - Session timeout enforcement
 * - NO fallback to insecure storage (base64) - fails securely
 */

import { xchacha20poly1305 } from '@noble/ciphers/chacha.js';
import { randomBytes, utf8ToBytes, bytesToUtf8 } from '@noble/ciphers/utils.js';
import { LogService } from './logService';

// Flag to track if encryption is available
let encryptionAvailable: boolean | null = null;

/**
 * Check if Web Crypto API is available for secure storage
 */
const checkEncryptionAvailable = (): boolean => {
  if (encryptionAvailable !== null) return encryptionAvailable;

  try {
    // Check for crypto.subtle (Web Crypto API)
    if (typeof crypto === 'undefined' || !crypto.subtle) {
      LogService.error('SecureStorage', 'Web Crypto API not available - secure storage disabled');
      encryptionAvailable = false;
      return false;
    }
    encryptionAvailable = true;
    return true;
  } catch {
    LogService.error('SecureStorage', 'Failed to check encryption availability');
    encryptionAvailable = false;
    return false;
  }
};

/**
 * Check if we're running in a secure context (HTTPS or localhost)
 */
export const isSecureContext = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.isSecureContext ||
         window.location.protocol === 'https:' ||
         window.location.hostname === 'localhost' ||
         window.location.hostname === '127.0.0.1';
};

// In-memory token storage (survives only as long as the tab is open)
let inMemoryTokens: {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
} = {
  accessToken: null,
  refreshToken: null,
  expiresAt: null,
};

// In-memory FULL session storage (for returning complete session object)
let inMemoryFullSession: string | null = null;

// Device fingerprint for encryption key derivation
const getDeviceFingerprint = (): string => {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.colorDepth.toString(),
    screen.width.toString(),
    screen.height.toString(),
    new Date().getTimezoneOffset().toString(),
  ];
  return components.join('|');
};

// Derive encryption key from device fingerprint
const deriveKey = async (): Promise<Uint8Array> => {
  const fingerprint = getDeviceFingerprint();
  const encoder = new TextEncoder();
  const data = encoder.encode(fingerprint);

  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(hashBuffer);
};

// Encrypt data for storage - NO FALLBACK to insecure methods
const encryptForStorage = async (data: string): Promise<string | null> => {
  if (!checkEncryptionAvailable()) {
    LogService.error('SecureStorage', 'Cannot encrypt - Web Crypto not available');
    return null;
  }

  try {
    const key = await deriveKey();
    const nonce = randomBytes(24);
    const cipher = xchacha20poly1305(key, nonce);
    const encrypted = cipher.encrypt(utf8ToBytes(data));

    // Combine nonce + encrypted data
    const combined = new Uint8Array(nonce.length + encrypted.length);
    combined.set(nonce);
    combined.set(encrypted, nonce.length);

    return btoa(String.fromCharCode(...combined));
  } catch (error) {
    // SECURITY: Do NOT fallback to insecure base64 encoding
    LogService.error('SecureStorage', 'Encryption failed - data will NOT be persisted', {
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    return null;
  }
};

// Decrypt data from storage - with legacy base64 support for migration
const decryptFromStorage = async (encryptedData: string): Promise<string | null> => {
  if (!checkEncryptionAvailable()) {
    LogService.warn('SecureStorage', 'Cannot decrypt - Web Crypto not available');
    return null;
  }

  try {
    const key = await deriveKey();
    const combined = Uint8Array.from(atob(encryptedData), (c) => c.charCodeAt(0));

    const nonce = combined.slice(0, 24);
    const encrypted = combined.slice(24);

    const cipher = xchacha20poly1305(key, nonce);
    const decrypted = cipher.decrypt(encrypted);

    return bytesToUtf8(decrypted);
  } catch {
    // MIGRATION: Try legacy base64 decode for old sessions, then re-encrypt
    try {
      const decoded = atob(encryptedData);
      // Verify it's valid JSON (session data)
      JSON.parse(decoded);
      LogService.warn('SecureStorage', 'Found legacy base64 session - will be re-encrypted on next save');
      return decoded;
    } catch {
      LogService.error('SecureStorage', 'Decryption failed - invalid or corrupted data');
      return null;
    }
  }
};

// Storage key prefix
const STORAGE_PREFIX = 'wealthhub-secure-';

/**
 * Secure storage adapter for Supabase
 * Implements the Storage interface expected by Supabase client
 */
export const secureStorage = {
  getItem: async (key: string): Promise<string | null> => {
    // First check in-memory
    if (key.includes('auth') && inMemoryTokens.accessToken) {
      // Check if token is expired
      if (inMemoryTokens.expiresAt && Date.now() > inMemoryTokens.expiresAt) {
        // Token expired, clear it
        inMemoryTokens = { accessToken: null, refreshToken: null, expiresAt: null };
      } else {
        // Return in-memory session data
        try {
          const sessionData = {
            access_token: inMemoryTokens.accessToken,
            refresh_token: inMemoryTokens.refreshToken,
            expires_at: inMemoryTokens.expiresAt ? Math.floor(inMemoryTokens.expiresAt / 1000) : null,
          };
          return JSON.stringify(sessionData);
        } catch {
          // Fall through to localStorage
        }
      }
    }

    // Fallback to encrypted localStorage
    const encrypted = localStorage.getItem(STORAGE_PREFIX + key);
    if (!encrypted) return null;

    return await decryptFromStorage(encrypted);
  },

  setItem: async (key: string, value: string): Promise<void> => {
    // Parse the session data if it's auth-related
    if (key.includes('auth')) {
      try {
        const session = JSON.parse(value);
        if (session.access_token) {
          inMemoryTokens.accessToken = session.access_token;
          inMemoryTokens.refreshToken = session.refresh_token || null;
          inMemoryTokens.expiresAt = session.expires_at
            ? session.expires_at * 1000
            : Date.now() + 3600000; // 1 hour default
        }
      } catch {
        // Not a session object, continue
      }
    }

    // Also store encrypted in localStorage for persistence across refreshes
    const encrypted = await encryptForStorage(value);
    if (encrypted) {
      localStorage.setItem(STORAGE_PREFIX + key, encrypted);
    } else {
      // SECURITY: If encryption fails, don't persist to localStorage
      // Session will still work in-memory for the current tab
      LogService.warn('SecureStorage', 'Session stored in memory only (encryption unavailable)');
    }
  },

  removeItem: (key: string): void => {
    // Clear in-memory tokens
    if (key.includes('auth')) {
      inMemoryTokens = { accessToken: null, refreshToken: null, expiresAt: null };
    }

    localStorage.removeItem(STORAGE_PREFIX + key);
  },
};

/**
 * Synchronous storage wrapper for Supabase compatibility
 * (Supabase expects synchronous storage, so we use a sync wrapper)
 *
 * Strategy:
 * - Keep tokens in memory for fast access during session
 * - Use base64 encoding for localStorage persistence (sync-friendly)
 * - On page load, restore from localStorage to memory
 */
export const syncSecureStorage = {
  getItem: (key: string): string | null => {
    // Check in-memory first - return FULL session if available
    if (key.includes('auth') && inMemoryFullSession && inMemoryTokens.accessToken) {
      // Check if token is expired
      if (inMemoryTokens.expiresAt && Date.now() > inMemoryTokens.expiresAt) {
        // Token expired, clear memory cache
        inMemoryTokens = { accessToken: null, refreshToken: null, expiresAt: null };
        inMemoryFullSession = null;
      } else {
        // Return the FULL session (includes user object)
        return inMemoryFullSession;
      }
    }

    // Fallback to localStorage - decode base64
    const stored = localStorage.getItem(STORAGE_PREFIX + key);
    if (!stored) return null;

    let decoded: string;
    try {
      // Try to decode base64
      decoded = atob(stored);
      // Verify it's valid JSON after decoding
      JSON.parse(decoded);
    } catch {
      // Not valid base64 or the decoded result isn't JSON
      // Try treating it as raw JSON
      try {
        JSON.parse(stored);
        decoded = stored;
      } catch {
        // Neither base64 nor valid JSON
        return null;
      }
    }

    // Restore to in-memory if it's auth data
    if (key.includes('auth')) {
      try {
        const parsed = JSON.parse(decoded);
        // Handle potential wrapped session format
        const session = parsed?.currentSession || parsed;
        if (session?.access_token) {
          // Store tokens for expiry checking
          inMemoryTokens.accessToken = session.access_token;
          inMemoryTokens.refreshToken = session.refresh_token || null;
          inMemoryTokens.expiresAt = session.expires_at
            ? session.expires_at * 1000
            : Date.now() + 3600000;
          // Store FULL session for returning complete object
          // If it was wrapped, we need to return the unwrapped session
          inMemoryFullSession = parsed?.currentSession ? JSON.stringify(session) : decoded;
        }
      } catch {
        // Not a valid session, just return the decoded value
      }
    }

    return decoded;
  },

  setItem: (key: string, value: string): void => {
    // Store in memory immediately
    if (key.includes('auth')) {
      try {
        const session = JSON.parse(value);
        if (session.access_token) {
          // Store tokens for expiry checking
          inMemoryTokens.accessToken = session.access_token;
          inMemoryTokens.refreshToken = session.refresh_token || null;
          inMemoryTokens.expiresAt = session.expires_at
            ? session.expires_at * 1000
            : Date.now() + 3600000;
          // Store FULL session for returning complete object
          inMemoryFullSession = value;
        }
      } catch {
        // Not a session object
      }
    }

    // Store base64 encoded in localStorage (sync, simple)
    // SECURITY: No fallback to raw storage - fail securely
    try {
      const encoded = btoa(value);
      localStorage.setItem(STORAGE_PREFIX + key, encoded);
    } catch (error) {
      // SECURITY: Do NOT store raw data - session will only work in memory
      LogService.error('SecureStorage', 'Base64 encoding failed - session stored in memory only', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      // Session still works in-memory for current tab
    }
  },

  removeItem: (key: string): void => {
    if (key.includes('auth')) {
      inMemoryTokens = { accessToken: null, refreshToken: null, expiresAt: null };
      inMemoryFullSession = null;
    }
    localStorage.removeItem(STORAGE_PREFIX + key);
  },
};

/**
 * Clear all secure storage (for logout)
 */
export const clearSecureStorage = (): void => {
  inMemoryTokens = { accessToken: null, refreshToken: null, expiresAt: null };
  inMemoryFullSession = null;

  // Clear all prefixed items
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(STORAGE_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
};

/**
 * Get current access token (for API calls)
 */
export const getAccessToken = (): string | null => {
  if (inMemoryTokens.expiresAt && Date.now() > inMemoryTokens.expiresAt) {
    return null; // Token expired
  }
  return inMemoryTokens.accessToken;
};

/**
 * Check if we have a valid session
 */
export const hasValidSession = (): boolean => {
  if (!inMemoryTokens.accessToken) return false;
  if (inMemoryTokens.expiresAt && Date.now() > inMemoryTokens.expiresAt) return false;
  return true;
};

/**
 * Session timeout handler
 * Clears tokens after inactivity
 */
let inactivityTimeout: number | null = null;
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes

export const resetInactivityTimer = (): void => {
  if (inactivityTimeout) {
    clearTimeout(inactivityTimeout);
  }

  if (inMemoryTokens.accessToken) {
    inactivityTimeout = window.setTimeout(() => {
      // Clear tokens after inactivity
      inMemoryTokens = { accessToken: null, refreshToken: null, expiresAt: null };
    }, INACTIVITY_TIMEOUT);
  }
};

// Reset timer on user activity
if (typeof window !== 'undefined') {
  ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach((event) => {
    window.addEventListener(event, resetInactivityTimer, { passive: true });
  });
}
